/**
 * Account and Site Data Hooks for Installation Jobs
 */

import { useQuery } from '@tanstack/react-query';
import { getAccounts, getAccountSites } from '@/features/accounts/api';
import type { Account, SiteDetail } from '@/features/accounts/types';

interface UseAccountsParams {
  status?: string;
  type?: string;
  types?: string;
  search?: string;
  pageLength?: number;
}

interface AccountsResponse {
  accounts: Account[];
  total: number;
}

interface SitesResponse {
  sites: SiteDetail[];
  total: number;
}

/**
 * Fetch all accounts by paginating through all pages (backend caps pageLength at 100)
 */
export function useAccounts(params?: UseAccountsParams) {
  return useQuery<AccountsResponse>({
    queryKey: ['accounts', 'list', params],
    queryFn: async () => {
      const pageLength = 100;
      const first = await getAccounts({
        status: params?.status,
        type: params?.type,
        types: params?.types,
        search: params?.search,
        pageNumber: 0,
        pageLength,
      });

      const allAccounts = [...first.accounts];

      if (first.total > pageLength) {
        const totalPages = Math.ceil(first.total / pageLength);
        const remaining = await Promise.all(
          Array.from({ length: totalPages - 1 }, (_, i) =>
            getAccounts({
              status: params?.status,
              type: params?.type,
              types: params?.types,
              search: params?.search,
              pageNumber: i + 1,
              pageLength,
            })
          )
        );
        for (const page of remaining) {
          allAccounts.push(...page.accounts);
        }
      }

      return { accounts: allAccounts, total: first.total };
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Fetch all sites for an account by paginating through all pages (backend caps pageLength at 100)
 */
export function useAccountSites(accountId: string | null) {
  return useQuery<SitesResponse>({
    queryKey: ['accounts', accountId, 'sites'],
    queryFn: async () => {
      if (!accountId) {
        return { sites: [], total: 0 };
      }

      const pageLength = 100;
      const first = await getAccountSites(accountId, { pageNumber: 0, pageLength });
      const allSites = [...first.data];

      if (first.total > pageLength) {
        const totalPages = Math.ceil(first.total / pageLength);
        const remaining = await Promise.all(
          Array.from({ length: totalPages - 1 }, (_, i) =>
            getAccountSites(accountId, { pageNumber: i + 1, pageLength })
          )
        );
        for (const page of remaining) {
          allSites.push(...page.data);
        }
      }

      return { sites: allSites, total: first.total };
    },
    enabled: !!accountId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}
